import { GET_UNREAD_NOTIFICATIONS } from '@/graphql/notifications';
import StorageSync from './storageSync';

class NotificationService {
  constructor(apolloClient) {
    this.apolloClient = apolloClient;
    this.eventSource = null;
    this.pollingInterval = null;
    this.listeners = new Set();
    this.lastNotificationId = null;
    this.processedNotificationIds = new Set(); // Track processed notifications
    this.mode = process.env.VUE_APP_NOTIFICATIONS_REALTIME || 'polling';
    this.isPageVisible = true;
    this.lastCheckTime = 0; // Track last check to prevent rapid duplicate requests
    this.minCheckInterval = 5000; // Minimum 5 seconds between checks
    this.storageSync = new StorageSync(this); // Cross-tab polling coordination
    
    // Load processed notifications from localStorage
    this.loadProcessedNotifications();
    
    // Pause polling when tab is hidden, resume when visible
    this.setupVisibilityListener();
  }

  /**
   * Setup visibility listener to pause/resume polling
   */
  setupVisibilityListener() {
    document.addEventListener('visibilitychange', () => {
      this.isPageVisible = !document.hidden;
      if (this.isPageVisible) {
        console.log('📱 Page visible - resuming polling');
        if (!this.pollingInterval) {
          this.startPolling();
        }
      } else {
        console.log('📱 Page hidden - pausing polling');
        if (this.pollingInterval) {
          clearInterval(this.pollingInterval);
          this.pollingInterval = null;
        }
      }
    });
  }

  /**
   * Start listening for notifications (polling only for now)
   */
  startListening() {
    console.log('🚀 Starting notification listening in polling mode');
    this.startPolling();
  }

  /**
   * Start Server-Sent Events for notifications
   */
  startSSE() {
    if (this.eventSource) {
      return; // Already connected
    }
    console.log('🔌 Starting SSE connection...');
    
    try {
      const token = localStorage.getItem('access_token');
      const url = `${process.env.VUE_APP_API_URL || 'http://localhost:8000/'}api/notifications/stream`;
      
      this.eventSource = new EventSource(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      this.eventSource.onopen = () => {
        console.log('✅ SSE connection opened successfully');
      };
      
      this.eventSource.onmessage = (event) => {
        console.log('📡 SSE message received:', event.data);
        try {
          const notification = JSON.parse(event.data);
          console.log('🎉 Notification received via SSE:', notification);
          
          // Only process actual notifications, not connection messages
          if (notification.type !== 'connected') {
            this.notifyListeners(notification);
          }
        } catch (error) {
          console.error('❌ Error parsing SSE data:', error);
        }
      };
      
      this.eventSource.onerror = (error) => {
        console.error('❌ SSE connection error:', error);
        this.eventSource.close();
        this.eventSource = null;
        // Fallback to polling
        console.log('🔄 Falling back to polling...');
        this.startPolling();
      };
      
    } catch (error) {
      console.error('❌ Failed to start SSE, falling back to polling:', error);
      this.startPolling();
    }
  }

  /**
   * Start polling for notifications
   */
  startPolling() {
    if (this.pollingInterval) {
      console.log('⚠️ Already polling, skipping...');
      return; // Already polling
    }
    
    if (!this.isPageVisible) {
      console.log('⚠️ Page is hidden, skipping polling start');
      return;
    }
    
    console.log('🚀 STARTING NOTIFICATION POLLING...');
    
    // Do an immediate check first
    console.log('🔍 Doing immediate notification check...');
    this.checkForNewNotifications();
    
    this.pollingInterval = setInterval(() => {
      // Only poll if page is visible and this tab is the leader (for multi-tab scenarios)
      if (this.isPageVisible && this.storageSync.shouldPoll()) {
        console.log('⏰ POLLING FOR NEW NOTIFICATIONS...', new Date().toISOString());
        this.checkForNewNotifications();
      }
    }, 10000); // Poll every 10 seconds
    
    console.log('✅ Polling interval set:', this.pollingInterval);
  }

  /**
   * Check for new notifications
   */
  async checkForNewNotifications() {
    // Prevent rapid duplicate requests from multiple triggers
    const now = Date.now();
    if (now - this.lastCheckTime < this.minCheckInterval) {
      console.log('⏱️ Skipping check - too soon since last check (', now - this.lastCheckTime, 'ms)');
      return;
    }
    this.lastCheckTime = now;

    try {
      console.log('🔍 CHECKING FOR NEW NOTIFICATIONS...', new Date().toISOString());
      console.log('Apollo client available:', !!this.apolloClient);
      
      const response = await this.apolloClient.query({
        query: GET_UNREAD_NOTIFICATIONS,
        fetchPolicy: 'network-only',
      });
      
      console.log('📡 GraphQL response received');
      const notifications = response.data.notifications || [];
      console.log('📋 Found unread notifications:', notifications.length);
      
      if (notifications.length > 0) {
        let foundNewNotification = false;
        
        for (const notification of notifications) {
          // Skip if we've already processed this notification
          if (this.processedNotificationIds.has(notification.id)) {
            console.log('⏭️ Skipping already processed notification:', notification.id);
            continue;
          }
          
          console.log('🎉 NEW NOTIFICATION FOUND! ID:', notification.id);
          console.log('📋 Notification details:', JSON.stringify(notification, null, 2));
          
          // Mark as processed
          this.processedNotificationIds.add(notification.id);
          this.lastNotificationId = notification.id;
          
          // Notify listeners immediately
          console.log('📢 About to notify listeners...');
          this.notifyListeners(notification);
          foundNewNotification = true;
        }
        
        if (!foundNewNotification) {
          console.log('ℹ️ All unread notifications already processed');
        }
        
        // Batch save after processing all notifications
        this.saveProcessedNotifications();
      } else {
        console.log('No unread notifications found');
      }
    } catch (error) {
      console.error('Error checking for new notifications:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
    }
  }

  /**
   * Force check for new notifications (useful for testing)
   */
  forceCheck() {
    console.log('Force checking for new notifications...');
    this.checkForNewNotifications();
  }

  /**
   * Load processed notifications from localStorage
   */
  loadProcessedNotifications() {
    try {
      const stored = localStorage.getItem('processedNotificationIds');
      if (stored) {
        const ids = JSON.parse(stored);
        this.processedNotificationIds = new Set(ids);
        console.log('📥 Loaded processed notifications from localStorage:', ids.length, 'notifications');
      }
    } catch (error) {
      console.error('Error loading processed notifications:', error);
    }
  }

  /**
   * Save processed notifications to localStorage
   */
  saveProcessedNotifications() {
    try {
      const ids = Array.from(this.processedNotificationIds);
      localStorage.setItem('processedNotificationIds', JSON.stringify(ids));
      console.log('💾 Saved processed notifications to localStorage:', ids.length, 'notifications');
    } catch (error) {
      console.error('Error saving processed notifications:', error);
    }
  }

  /**
   * Clear processed notifications cache (useful for testing)
   */
  clearProcessedCache() {
    console.log('🧹 Clearing processed notification cache...');
    this.processedNotificationIds.clear();
    this.lastNotificationId = null;
    localStorage.removeItem('processedNotificationIds');
    console.log('🗑️ Cleared processed notifications from localStorage');
  }

  /**
   * Clear old test notifications from localStorage cache
   */
  clearOldTestNotifications() {
    console.log('🧹 Clearing old test notifications from cache...');
    
    // Get all processed notification IDs
    const processedIds = Array.from(this.processedNotificationIds);
    
    // Filter out test notifications
    const filteredIds = processedIds.filter(() => {
      // We can't check the content here, but we can clear all old processed IDs
      // The isNewNotification method will handle filtering old notifications
      return true; // Keep all for now, let isNewNotification handle filtering
    });
    
    // Update the processed set
    this.processedNotificationIds = new Set(filteredIds);
    this.saveProcessedNotifications();
    
    console.log('🗑️ Cleared old test notifications from cache');
  }

  /**
   * Test method to simulate a notification (for debugging)
   */
  testNotification() {
    console.log('Testing notification display...');
    const testNotification = {
      id: 'test-123',
      type: 'system',
      data: {
        title: 'Test Notification',
        message: 'This is a test notification from the frontend'
      },
      created_at: new Date().toISOString(),
      read_at: null
    };
    this.notifyListeners(testNotification);
  }

  /**
   * Stop listening for notifications
   */
  stopListening() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Add a listener for new notifications
   */
  addListener(listener) {
    this.listeners.add(listener);
  }

  /**
   * Remove a listener
   */
  removeListener(listener) {
    this.listeners.delete(listener);
  }

  /**
   * Notify all listeners
   */
  notifyListeners(notification) {
    console.log('=== NOTIFYING LISTENERS ===');
    console.log('Notification object:', notification);
    console.log('Number of listeners:', this.listeners.size);
    console.log('Listeners:', Array.from(this.listeners));
    
    this.listeners.forEach((listener, index) => {
      try {
        console.log(`Calling listener ${index + 1}...`);
        listener(notification);
        console.log(`Listener ${index + 1} called successfully`);
      } catch (error) {
        console.error(`Notification listener ${index + 1} error:`, error);
      }
    });
    
    console.log('=== END NOTIFYING LISTENERS ===');
  }

  /**
   * Show a browser notification
   */
  showBrowserNotification(notification) {
    if (!('Notification' in window)) {
      return;
    }

    if (Notification.permission === 'granted') {
      const title = this.getNotificationTitle(notification);
      const body = this.getNotificationMessage(notification);
      
      new Notification(title, {
        body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: notification.id,
      });
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          this.showBrowserNotification(notification);
        }
      });
    }
  }

  /**
   * Get notification title based on type
   */
  getNotificationTitle(notification) {
    if (!notification) {
      return 'New Notification';
    }
    
    const titles = {
      'stock_request': 'New Stock Request',
      'offer': 'New Offer Received',
      'system': 'System Notification',
      'user': 'User Notification',
    };
    
    return titles[notification.type] || 'New Notification';
  }

  /**
   * Get notification message from data
   */
  getNotificationMessage(notification) {
    console.log('Getting message for notification:', notification);
    console.log('Notification type:', notification?.type);
    console.log('Notification data type:', typeof notification?.data);
    console.log('Notification data:', notification?.data);
    
    if (!notification) {
      return 'You have a new notification';
    }
    
    // Check if data is a string (JSON) that needs parsing
    if (typeof notification.data === 'string') {
      try {
        const parsedData = JSON.parse(notification.data);
        console.log('Parsed data from string:', parsedData);
        if (parsedData.message) {
          return parsedData.message;
        }
        if (parsedData.title) {
          return parsedData.title;
        }
      } catch (e) {
        console.log('Failed to parse data string:', e);
      }
    }
    
    // Check if data is an object
    if (notification.data && typeof notification.data === 'object') {
      if (notification.data.message) {
        console.log('Found message in data.message:', notification.data.message);
        return notification.data.message;
      }
      
      if (notification.data.title) {
        console.log('Found title in data.title:', notification.data.title);
        return notification.data.title;
      }
    }
    
    if (notification.message) {
      console.log('Found message in notification.message:', notification.message);
      return notification.message;
    }
    
    console.log('No message found, using default');
    return 'You have a new notification';
  }

  /**
   * Request browser notification permission
   */
  requestPermission() {
    if ('Notification' in window && Notification.permission === 'default') {
      return Notification.requestPermission();
    }
    return Promise.resolve(Notification.permission);
  }
}

export default NotificationService; 